import { Plus } from 'lucide-react';

import type { ScheduleEvent } from '../../../services/schedule';

import {
  DAY_NAMES,
  isToday,
  monthGridRange,
  toDateKey,
} from './scheduleDateUtils';

interface MonthCalendarProps {
  anchor: Date;
  eventsByDate: Record<string, ScheduleEvent[]>;
  enrolledClassIds: Set<string>;
  isOwner: boolean;
  isLoading: boolean;
  onEventClick: (event: ScheduleEvent) => void;
  onDayClick: (date: Date) => void;
  onCreateClass?: (date: string) => void;
}

const MAX_VISIBLE_PER_DAY = 3;

function formatChipTime(iso: string | null): string {
  if (!iso) return '';
  return new Date(iso).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
}

export function MonthCalendar({
  anchor,
  eventsByDate,
  enrolledClassIds,
  isOwner,
  isLoading,
  onEventClick,
  onDayClick,
  onCreateClass,
}: MonthCalendarProps) {
  const { days } = monthGridRange(anchor);
  const currentMonth = anchor.getMonth();

  return (
    <div className="flex flex-col h-full min-h-0">
      {/* Weekday header */}
      <div className="grid grid-cols-7 gap-px bg-[#363232] rounded-t-xl overflow-hidden shrink-0">
        {DAY_NAMES.map((name) => (
          <div
            key={name}
            className="bg-[#1A1717] text-[10px] uppercase tracking-wider font-semibold text-[#A9A3A3] text-center py-2"
          >
            {name}
          </div>
        ))}
      </div>

      {/* 6-row grid */}
      <div className="grid grid-cols-7 grid-rows-6 gap-px bg-[#363232] rounded-b-xl overflow-hidden flex-1 min-h-0">
        {days.map((day) => {
          const key = toDateKey(day);
          const dayEvents = eventsByDate[key] ?? [];
          const today = isToday(day);
          const inMonth = day.getMonth() === currentMonth;
          const visible = dayEvents.slice(0, MAX_VISIBLE_PER_DAY);
          const overflow = dayEvents.length - visible.length;

          return (
            <div
              key={key}
              className={`flex flex-col min-h-0 min-w-0 p-1 group ${
                inMonth ? 'bg-[#1A1717]' : 'bg-[#141212]'
              } ${today ? 'ring-1 ring-inset ring-[#9B1421]/60' : ''}`}
            >
              <div className="flex items-center justify-between shrink-0">
                <button
                  type="button"
                  onClick={() => onDayClick(day)}
                  className={`text-xs font-semibold leading-none rounded px-1 py-0.5 hover:bg-[#363232] transition-colors ${
                    today
                      ? 'text-[#9B1421]'
                      : inMonth
                        ? 'text-[#EBFFEE]'
                        : 'text-[#504A4A]'
                  }`}
                >
                  {day.getDate()}
                </button>
                {isOwner && onCreateClass && (
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      onCreateClass(key);
                    }}
                    className="opacity-0 group-hover:opacity-100 p-0.5 text-[#504A4A] hover:text-[#9B1421] transition-all rounded"
                    aria-label="Create class on this day"
                  >
                    <Plus size={11} />
                  </button>
                )}
              </div>

              <div className="flex-1 overflow-hidden mt-0.5 space-y-0.5 min-h-0">
                {isLoading && dayEvents.length === 0 ? (
                  <div className="h-3 bg-[#2A2626] rounded animate-pulse" />
                ) : (
                  <>
                    {visible.map((event) => {
                      const enrolled = enrolledClassIds.has(event.event_id);
                      return (
                        <button
                          key={event.event_id}
                          onClick={(e) => {
                            e.stopPropagation();
                            onEventClick(event);
                          }}
                          className={`w-full text-left rounded px-1 py-0.5 text-[10px] leading-tight truncate flex items-center gap-1 transition-colors ${
                            enrolled
                              ? 'bg-[#9B1421]/25 text-[#EBFFEE] hover:bg-[#9B1421]/40'
                              : 'bg-[#2A2626] text-[#EBFFEE] hover:bg-[#363232]'
                          }`}
                          title={`${event.sport_name ?? 'Class'} · ${formatChipTime(event.start_time)}`}
                        >
                          <span className="text-[9px] text-[#A9A3A3] shrink-0">
                            {formatChipTime(event.start_time)}
                          </span>
                          <span className="truncate">
                            {event.sport_name ?? 'Class'}
                          </span>
                        </button>
                      );
                    })}
                    {overflow > 0 && (
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          onDayClick(day);
                        }}
                        className="w-full text-left text-[10px] text-[#A9A3A3] hover:text-[#EBFFEE] px-1"
                      >
                        +{overflow} more
                      </button>
                    )}
                  </>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
